import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { SCSidebar, SCSidebarItem, SCTooltip } from 'rollup-allspark';
import klikLogo from '../../assets/images/klikit-admin-black.png';
import {
  ADD_ORDER_CONFIG_PAYMENT_METHOD_V2,
  CAMPAIGN_ROUTE,
  CUSTOMER_LIST_ROUTE,
  ESB_MENU_MAPPING_ROUTE,
  IMAGE_MANAGER_ROUTE,
  INVENTORY_COMPOSITE_ITEMS_ROUTE,
  INVENTORY_DELIVERY_ORDERS_ROUTE,
  INVENTORY_INPUTS_ROUTE,
  INVENTORY_ITEM_SETUP_ROUTE,
  INVENTORY_PRODUCTION_RUNS_ROUTE,
  INVENTORY_PURCHASE_ORDERS_ROUTE,
  INVENTORY_ROUTE,
  INVENTORY_SETTINGS_ROUTE,
  INVENTORY_SUPPLIER_DATABASE_ROUTE,
  INVENTORY_WASTE_ROUTE,
  INVOICE_ROUTE,
  KAI_ADMIN_ROUTE,
  LINKS_IN_BIO_ROUTE,
  LIVE_MONITOR_ROUTE,
  MENU_PULLING_ROUTE,
  MENU_V2_ROUTE,
  MIRROR_ORDER_ROUTE,
  ORDER_AMENDER_ROUTE,
  PENDING_PAYMENT_ROUTE,
  PAYMENT_INTEGRATION_ROUTE,
  PROVIDER_INTEGRATION_ROUTE,
  ENGAGEIT_INBOX_ROUTE,
  PIXEL_TRACKING_ROUTE,
  RATING_REVIEW_INTEGRATION_ROUTE,
  RESERVATION_ROUTE,
  WHITELABEL_ROUTE,
} from '../../config/constants/routes';
import {
  IS_INVENTORY_PROCUREMENT_ENABLED,
  IS_MENU_TAB_DISABLED,
} from '../../config/env';
import { makeSelectGlobalProperty } from '../../containers/App/selectors';
import { canViewKai as canViewKaiGate } from '../../containers/Kai/canViewKai';
import KaiDrawer from '../../containers/Kai/Drawer';
import { initialState as oniInitialState } from '../../containers/ONI/reducer';
import { isEmpty } from '../../utils';
import { hasPermission, PERMISSIONS } from '../../utils/permission';

// Selector to get isKDSViewActive from ONI state
const selectONIDomain = state => state.oni || oniInitialState;
const selectIsKDSViewActive = state => selectONIDomain(state).isKDSViewActive;

const SideItemDisableWrapper = ({ children }) => (
  <SCTooltip content="Due to maintenance, menu section is disabled currently. Check back later.">
    <div className="cursor-not-allowed opacity-50">
      <div className="pointer-events-none">{children}</div>
    </div>
  </SCTooltip>
);

function Layout({
  appSettings,
  businessList,
  children,
  user,
  isKDSViewActive,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const previousCollapseState = useRef(false);
  const loc = useLocation();
  const locPath = loc.pathname.split('/');
  // Auto-expand POS Mapping if on ESB Mapping page
  const [posMappingExpanded, setPosMappingExpanded] = useState(
    loc.pathname.includes('esb-mapping'),
  );
  // Auto-expand Inventory group if on any /inventory/* page
  const [inventoryExpanded, setInventoryExpanded] = useState(
    loc.pathname.startsWith('/inventory'),
  );

  // Auto-collapse sidebar when KDS view is active (only on ONI page)
  useEffect(() => {
    const isOnONIPage = loc.pathname.includes('/oni');
    if (isKDSViewActive && isOnONIPage) {
      // Save current state before collapsing
      previousCollapseState.current = isCollapsed;
      setIsCollapsed(true);
    } else if (
      !isKDSViewActive &&
      previousCollapseState.current !== isCollapsed
    ) {
      // Restore previous state when KDS is deactivated
      // Only restore if we're coming from KDS mode (previousCollapseState was saved)
      if (previousCollapseState.current === false && isOnONIPage) {
        setIsCollapsed(false);
      }
    }
  }, [isKDSViewActive, loc.pathname]);
  // const itemsRoute = '/menu/items';
  // const modifiersRoute = '/menu/modifiers';
  // const rulesRoute = '/menu/rules';

  // Auto-collapse POS Mapping when navigating away from ESB pages
  React.useEffect(() => {
    if (!loc.pathname.includes('esb-mapping')) {
      setPosMappingExpanded(false);
    } else {
      setPosMappingExpanded(true);
    }
  }, [loc.pathname]);

  // Auto-expand Inventory group on /inventory/* pages, collapse when away
  React.useEffect(() => {
    if (loc.pathname.startsWith('/inventory')) {
      setInventoryExpanded(true);
    } else {
      setInventoryExpanded(false);
    }
  }, [loc.pathname]);

  const showMenuV2 = hasPermission(user, 'fetch.mmv2');
  const hasAccessToWhitelabel = hasPermission(user, 'crud.white_label');
  const showImageManager = hasPermission(user, 'fetch.image.manager');
  const isMenuV1Hidden = true;

  // const isMenuNavActive = (match, location) =>
  //   location.pathname === itemsRoute ||
  //   location.pathname === modifiersRoute ||
  //   location.pathname === rulesRoute;

  const logoVisibilityClass = `flex flex-1 items-center font-medium ${
    isCollapsed ? 'opacity-0' : ''
  }`;

  const headerWidth = isCollapsed ? 90 : 225;

  const {
    BILLING_USERS = [],
    CRM_USERS = [],
    POS_BUSINESSES,
    SUPER_ADMIN_USERS = [],
  } = appSettings;

  const showPosMapping =
    Array.isArray(POS_BUSINESSES) &&
    (isEmpty(POS_BUSINESSES) ||
      (Array.isArray(businessList) &&
        businessList.some(
          b => b?.id != null && POS_BUSINESSES.includes(b.id),
        )));

  const canViewCRM =
    hasPermission(user, 'fe.module.crm') &&
    (isEmpty(CRM_USERS) ||
      (!isEmpty(user) &&
        !isEmpty(CRM_USERS) &&
        CRM_USERS.includes(user.email)));

  const canViewBilling =
    hasPermission(user, 'fetch.invoice') &&
    (isEmpty(BILLING_USERS) ||
      (!isEmpty(user) &&
        !isEmpty(BILLING_USERS) &&
        BILLING_USERS.includes(user.email)));

  const canViewKai = canViewKaiGate(user, appSettings);

  const isSuperAdmin =
    isEmpty(SUPER_ADMIN_USERS) ||
    (!isEmpty(user) &&
      !isEmpty(SUPER_ADMIN_USERS) &&
      SUPER_ADMIN_USERS.includes(user.email));

  const header = (
    <div
      role="presentation"
      className="flex cursor-pointer"
      style={{ width: headerWidth }}
      onClick={() => setIsCollapsed(!isCollapsed)}
    >
      <div
        className="flex items-center justify-end mr-2"
        style={{
          width: '52px',
          minWidth: '44px',
          height: '60px',
          lineHeight: '60px',
        }}
      >
        <i className="kt-hamburger-menu text-grey-darker text-lg" />
      </div>
      <div className={logoVisibilityClass}>
        <img src={klikLogo} alt="Klikit" style={{ height: '22px' }} />
      </div>
    </div>
  );

  const versionNo = process.env.npm_package_version;

  const copyDisclaimer = !isCollapsed ? (
    <div className="copy-disclaimer p-8 w-full text-mini text-grey-darker">
      <div className="mb-1">Copyright © KLIKIT PTE LTD.</div>
      <div className="mb-1">All Rights Reserved</div>
      <div>v{versionNo}</div>
    </div>
  ) : null;

  const checkRoute = routeName => locPath.includes(routeName);

  const sidebarItemClass = isCollapsed ? 'opacity-0' : '';

  const menuSidebarItem = (
    <SCSidebarItem
      icon={<i className="kt-menu text-grey-darker" />}
      title={
        <NavLink title="Menu" to="/menu/items" className={sidebarItemClass}>
          Menu
        </NavLink>
      }
      active={checkRoute('menu')}
    />
  );

  const menuElV1 = IS_MENU_TAB_DISABLED ? (
    <SideItemDisableWrapper>{menuSidebarItem}</SideItemDisableWrapper>
  ) : (
    menuSidebarItem
  );

  const menuElV2 = showMenuV2 ? (
    <SCSidebarItem
      icon={<i className="kt-menu text-grey-darker" />}
      title={
        <NavLink
          title="Menu Management"
          to={MENU_V2_ROUTE}
          className={sidebarItemClass}
        >
          Menu
        </NavLink>
      }
      active={checkRoute('menu-v2')}
    />
  ) : null;

  const promoModuleEl = hasPermission(user, 'fetch.promo') ? (
    <SCSidebarItem
      icon={<i className="kt-promo text-grey-darker" />}
      title={
        <NavLink title="Promo" to="/promo" className={sidebarItemClass}>
          Promo
        </NavLink>
      }
      active={checkRoute('promo')}
    />
  ) : null;

  const campaignModuleEl = hasPermission(user, PERMISSIONS.FETCH_CAMPAIGN) ? (
    <SCSidebarItem
      icon={<i className="kt-discount text-grey-darker" />}
      title={
        <NavLink
          title="Campaign"
          to={CAMPAIGN_ROUTE}
          className={sidebarItemClass}
        >
          Campaign
        </NavLink>
      }
      active={checkRoute('campaign')}
    />
  ) : null;

  const loyaltyModuleEl = hasPermission(user, 'fetch.loyalty') ? (
    <SCSidebarItem
      icon={<i className="kt-gift text-grey-darker" />}
      title={
        <NavLink title="Loyalty" to="/loyalty" className={sidebarItemClass}>
          Loyalty
        </NavLink>
      }
      active={checkRoute('loyalty')}
    />
  ) : null;

  const reservationModuleEl = hasPermission(user, 'fetch.reservation') ? (
    <SCSidebarItem
      icon={<i className="kt-reservation text-grey-darker" />}
      title={
        <NavLink
          title="Reservation"
          to={RESERVATION_ROUTE}
          className={sidebarItemClass}
        >
          Reservation
        </NavLink>
      }
      active={checkRoute('reservation')}
    />
  ) : null;

  const crmModuleEl = !canViewCRM ? null : (
    <SCSidebarItem
      icon={<i className="kt-customer-settings text-grey-darker" />}
      title={
        <NavLink
          title="Customer Hub"
          to={CUSTOMER_LIST_ROUTE}
          className={sidebarItemClass}
        >
          Customer Hub
        </NavLink>
      }
      active={checkRoute('customers')}
    />
  );

  const invoiceModule = !canViewBilling ? null : (
    <SCSidebarItem
      icon={<i className="kt-invoice text-grey-darker" />}
      title={
        <NavLink
          title="Billing"
          to={INVOICE_ROUTE}
          className={sidebarItemClass}
        >
          Billing
        </NavLink>
      }
      active={checkRoute('invoice')}
    />
  );

  const whiteLabelModuleEl = hasAccessToWhitelabel ? (
    <SCSidebarItem
      icon={<i className="kt-white-label text-grey-darker" />}
      title={
        <NavLink
          title="Whitelabel"
          to={WHITELABEL_ROUTE}
          className={sidebarItemClass}
        >
          White Label
        </NavLink>
      }
      active={checkRoute('whitelabel')}
    />
  ) : null;

  const imageManagerModuleEl = showImageManager && (
    <SCSidebarItem
      icon={<i className="kt-image text-grey-darker" />}
      title={
        <NavLink
          title="Image Manager"
          to={IMAGE_MANAGER_ROUTE}
          className={sidebarItemClass}
        >
          Image Manager
        </NavLink>
      }
      active={checkRoute('image-manager')}
    />
  );

  const linkinBioModuleEl = hasPermission(user, 'fetch.linkinbio') ? (
    <SCSidebarItem
      icon={<i className="kt-sync text-grey-darker" />}
      title={
        <NavLink
          title="Links"
          to={LINKS_IN_BIO_ROUTE}
          className={sidebarItemClass}
        >
          Links
        </NavLink>
      }
      active={checkRoute('links')}
    />
  ) : null;

  const reviewIntegrationModuleEl = hasPermission(
    user,
    'fetch.review.integration',
  ) ? (
    <SCSidebarItem
      icon={<i className="kt-article text-grey-darker" />}
      title={
        <NavLink
          title="Rating & Review Integration"
          to={RATING_REVIEW_INTEGRATION_ROUTE}
          className={sidebarItemClass}
        >
          Rating & Review
        </NavLink>
      }
      active={checkRoute('rating-review-integration')}
    />
  ) : null;

  const engageItModuleEl = hasPermission(user, PERMISSIONS.FETCH_ENGAGEIT) ? (
    <SCSidebarItem
      icon={<i className="kt-article text-grey-darker" />}
      title={
        <NavLink
          title="Engagement Hub"
          to={ENGAGEIT_INBOX_ROUTE}
          className={sidebarItemClass}
        >
          Engagement Hub
        </NavLink>
      }
      active={checkRoute('engageit')}
    />
  ) : null;

  const liveMonitorModuleEl = hasPermission(
    user,
    PERMISSIONS.FETCH_LIVE_MONITOR,
  ) ? (
    <SCSidebarItem
      icon={<i className="kt-eye-tracking text-grey-darker" />}
      title={
        <NavLink
          title="Live Monitor"
          to={LIVE_MONITOR_ROUTE}
          className={sidebarItemClass}
        >
          Live Monitor
        </NavLink>
      }
      active={checkRoute('live-monitor')}
    />
  ) : null;

  const paymentIntegrationModuleEl = (
    <SCSidebarItem
      icon={<i className="kt-payment text-grey-darker" />}
      title={
        <NavLink
          title="Payment Integration"
          to={PAYMENT_INTEGRATION_ROUTE}
          className={sidebarItemClass}
        >
          Payment Integration
        </NavLink>
      }
      active={checkRoute('payment-integration')}
    />
  );

  const menuPullingModuleEl = (
    <SCSidebarItem
      icon={<i className="kt-menu-pulling text-grey-darker" />}
      title={
        <NavLink
          title="Menu Pulling"
          to={MENU_PULLING_ROUTE}
          className={sidebarItemClass}
        >
          Menu Pulling
        </NavLink>
      }
      active={checkRoute('menu-pulling')}
    />
  );

  const webhookReplayModuleEl = hasPermission(
    user,
    PERMISSIONS.FETCH_WEBHOOK_REPLAY,
  ) ? (
    <SCSidebarItem
      icon={<i className="kt-webhooks text-grey-darker" />}
      title={
        <NavLink
          title="Webhook Replay"
          to="/webhook-replay"
          className={sidebarItemClass}
        >
          Webhook Replay
        </NavLink>
      }
      active={checkRoute('webhook-replay')}
    />
  ) : null;

  const addOrderSettingsEl = hasPermission(user, 'fetch.addOrderConfig') ? (
    <SCSidebarItem
      icon={<i className="kt-payment-setting text-grey-darker" />}
      title={
        <NavLink
          title="Add Order Config"
          to={ADD_ORDER_CONFIG_PAYMENT_METHOD_V2}
          className={sidebarItemClass}
        >
          Add Order Config
        </NavLink>
      }
      active={checkRoute('add-order-config')}
    />
  ) : null;

  const featureAccessModuleElm = hasPermission(
    user,
    PERMISSIONS.FETCH_FEATURE_ALL,
  ) ? (
    <SCSidebarItem
      icon={<i className="kt-flag text-grey-darker" />}
      title={
        <NavLink
          title="Feature Access"
          to="/feature-access"
          className={sidebarItemClass}
        >
          Feature Access
        </NavLink>
      }
      active={checkRoute('feature-access')}
    />
  ) : null;

  const roleSidebarElm = isSuperAdmin ? (
    <SCSidebarItem
      icon={<i className="kt-role text-grey-darker" />}
      title={
        <NavLink title="Role" to="/role" className={sidebarItemClass}>
          Role
        </NavLink>
      }
      active={checkRoute('role')}
    />
  ) : null;

  const permissionSidebarElm = isSuperAdmin ? (
    <SCSidebarItem
      icon={<i className="kt-permission text-grey-darker" />}
      title={
        <NavLink
          title="Permission"
          to="/permission"
          className={sidebarItemClass}
        >
          Permission
        </NavLink>
      }
      active={checkRoute('permission')}
    />
  ) : null;

  // kAI admin (provider config, quotas, usage) — klikit-internal only, same
  // gate as Role/Permission. Server-side RequireAdmin is the real enforcement.
  const kaiAdminSidebarElm = isSuperAdmin ? (
    <SCSidebarItem
      icon={<i className="kt-sparkles text-grey-darker" />}
      title={
        <NavLink
          title="kAI Admin"
          to={KAI_ADMIN_ROUTE}
          className={sidebarItemClass}
        >
          kAI Admin
        </NavLink>
      }
      active={checkRoute('kai-admin')}
    />
  ) : null;

  // API Integration items are gated on the matching permission. Platform
  // admins (isSuperAdmin) always see them; everyone else needs the
  // specific permission. Matches the partner-api RequirePermission
  // middleware on the equivalent endpoints.
  const canManagePartnerAccess =
    isSuperAdmin || hasPermission(user, PERMISSIONS.PARTNER_API_ACCESS);
  const canViewPartnerActivity =
    isSuperAdmin || hasPermission(user, PERMISSIONS.PARTNER_API_ACTIVITY);

  const partnerAccessSidebarElm = canManagePartnerAccess ? (
    <SCSidebarItem
      icon={<i className="kt-permission text-grey-darker" />}
      title={
        <NavLink
          title="Partner Access"
          to="/partner-access"
          className={sidebarItemClass}
        >
          Partner Access
        </NavLink>
      }
      active={checkRoute('partner-access')}
    />
  ) : null;

  const apiActivityLogSidebarElm = canViewPartnerActivity ? (
    <SCSidebarItem
      icon={<i className="kt-clock text-grey-darker" />}
      title={
        <NavLink
          title="Activity Log"
          to="/api-activity-log"
          className={sidebarItemClass}
        >
          Activity Log
        </NavLink>
      }
      active={checkRoute('api-activity-log')}
    />
  ) : null;

  return (
    <div className="h-screen flex w-full sc-main">
      <SCSidebar
        header={header}
        collapsed={isCollapsed}
        width={225}
        collapsedWidth={90}
      >
        <>
          <div className="menu-item-group-label">Orders</div>
          <SCSidebarItem
            icon={<i className="sc-dashboard text-grey-darker" />}
            title={
              <NavLink
                title="Order Dashboard"
                to="/oni"
                className={sidebarItemClass}
              >
                Overview
              </NavLink>
            }
            active={checkRoute('oni')}
          />
          <SCSidebarItem
            icon={<i className="sc-manual-order text-grey-darker" />}
            title={
              <NavLink
                title="Order Amender"
                to={ORDER_AMENDER_ROUTE}
                className={sidebarItemClass}
              >
                Amend
              </NavLink>
            }
            active={checkRoute('order-amender')}
          />
          <SCSidebarItem
            icon={<i className="kt-wallet text-grey-darker" />}
            title={
              <NavLink
                title="Pending Payment"
                to={PENDING_PAYMENT_ROUTE}
                className={sidebarItemClass}
              >
                Pending Payment
              </NavLink>
            }
            active={checkRoute('pending-payment')}
          />
        </>

        <>
          <div className="menu-item-group-label">Menu</div>
          {!isMenuV1Hidden && menuElV1}
          {menuElV2}
          <div
            className="cursor-pointer pos-mapping-sidebar-row"
            onClick={() => setInventoryExpanded(!inventoryExpanded)}
            role="button"
            tabIndex={0}
            onKeyPress={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                setInventoryExpanded(!inventoryExpanded);
              }
            }}
          >
            <SCSidebarItem
              icon={<i className="kt-package text-grey-darker" />}
              title={
                <span
                  className={`${sidebarItemClass} text-black font-medium text-sm antialiased`}
                >
                  Inventory
                </span>
              }
              active={checkRoute('inventory')}
            />
            {inventoryExpanded && (
              <div className="relative ml-6 pl-2 border-l-2 border-grey">
                <SCSidebarItem
                  icon={<i className="kt-analytics text-grey-darker" />}
                  title={
                    <NavLink
                      title="Overview"
                      to={INVENTORY_ROUTE}
                      className={sidebarItemClass}
                    >
                      Overview
                    </NavLink>
                  }
                  active={
                    loc.pathname === INVENTORY_ROUTE ||
                    loc.pathname.startsWith(`${INVENTORY_ROUTE}?`)
                  }
                />
                <div className="my-2 border-t border-grey" />
                <SCSidebarItem
                  icon={<i className="kt-menu text-grey-darker" />}
                  title={
                    <NavLink
                      title="Item Setup"
                      to={INVENTORY_ITEM_SETUP_ROUTE}
                      className={sidebarItemClass}
                    >
                      Item Setup
                    </NavLink>
                  }
                  active={checkRoute('inventory/item-setup')}
                />
                <SCSidebarItem
                  icon={<i className="kt-tools-kitchen text-grey-darker" />}
                  title={
                    <NavLink
                      title="Inputs"
                      to={INVENTORY_INPUTS_ROUTE}
                      className={sidebarItemClass}
                    >
                      Inputs
                    </NavLink>
                  }
                  active={checkRoute('inventory/inputs')}
                />
                <SCSidebarItem
                  icon={<i className="kt-columns text-grey-darker" />}
                  title={
                    <NavLink
                      title="Composite Items"
                      to={INVENTORY_COMPOSITE_ITEMS_ROUTE}
                      className={sidebarItemClass}
                    >
                      Composite Items
                    </NavLink>
                  }
                  active={checkRoute('inventory/composite-items')}
                />
                {IS_INVENTORY_PROCUREMENT_ENABLED && (
                  <>
                    <SCSidebarItem
                      icon={<i className="kt-invoice text-grey-darker" />}
                      title={
                        <NavLink
                          title="Purchase Orders"
                          to={INVENTORY_PURCHASE_ORDERS_ROUTE}
                          className={sidebarItemClass}
                        >
                          Purchase Orders
                        </NavLink>
                      }
                      active={checkRoute('inventory/purchase-orders')}
                    />
                    <SCSidebarItem
                      icon={<i className="kt-rider text-grey-darker" />}
                      title={
                        <NavLink
                          title="Delivery Orders"
                          to={INVENTORY_DELIVERY_ORDERS_ROUTE}
                          className={sidebarItemClass}
                        >
                          Delivery Orders
                        </NavLink>
                      }
                      active={checkRoute('inventory/delivery-orders')}
                    />
                    <SCSidebarItem
                      icon={<i className="kt-bin text-grey-darker" />}
                      title={
                        <NavLink
                          title="Waste"
                          to={INVENTORY_WASTE_ROUTE}
                          className={sidebarItemClass}
                        >
                          Waste
                        </NavLink>
                      }
                      active={checkRoute('inventory/waste')}
                    />
                    <SCSidebarItem
                      icon={<i className="kt-tools-kitchen text-grey-darker" />}
                      title={
                        <NavLink
                          title="Production Runs"
                          to={INVENTORY_PRODUCTION_RUNS_ROUTE}
                          className={sidebarItemClass}
                        >
                          Production Runs
                        </NavLink>
                      }
                      active={checkRoute('inventory/production-runs')}
                    />
                  </>
                )}
                <SCSidebarItem
                  icon={<i className="kt-building-store text-grey-darker" />}
                  title={
                    <NavLink
                      title="Suppliers"
                      to={INVENTORY_SUPPLIER_DATABASE_ROUTE}
                      className={sidebarItemClass}
                    >
                      Suppliers
                    </NavLink>
                  }
                  active={checkRoute('inventory/supplier-database')}
                />
                <SCSidebarItem
                  icon={<i className="kt-customer-settings text-grey-darker" />}
                  title={
                    <NavLink
                      title="Branch Settings"
                      to={INVENTORY_SETTINGS_ROUTE}
                      className={sidebarItemClass}
                    >
                      Branch Settings
                    </NavLink>
                  }
                  active={checkRoute('inventory/settings')}
                />
              </div>
            )}
          </div>
        </>

        <>
          <div className="menu-item-group-label">Marketing</div>
          <SCSidebarItem
            icon={<i className="sc-integration text-grey-darker" />}
            title={
              <NavLink
                title="Webshop"
                to="/webshop"
                className={sidebarItemClass}
              >
                Webshop
              </NavLink>
            }
            active={checkRoute('webshop')}
          />
          {linkinBioModuleEl}
          {promoModuleEl}
          {campaignModuleEl}
          {loyaltyModuleEl}
          {reservationModuleEl}
          {crmModuleEl}
          {engageItModuleEl}
          {hasPermission(user, PERMISSIONS.FETCH_PIXEL_TRACKING) ? (
            <SCSidebarItem
              icon={<i className="kt-analytics text-grey-darker" />}
              title={
                <NavLink
                  title="Pixel Tracking"
                  to={PIXEL_TRACKING_ROUTE}
                  className={sidebarItemClass}
                >
                  Pixel Tracking
                </NavLink>
              }
              active={checkRoute('pixel-tracking')}
            />
          ) : null}
          {whiteLabelModuleEl}
        </>

        <div>
          <div className="menu-item-group-label">Analytics</div>
          <SCSidebarItem
            icon={<i className="kt-analytics text-grey-darker" />}
            title={
              <NavLink
                title="Analytics"
                to="/analytics"
                className={sidebarItemClass}
              >
                Overview
              </NavLink>
            }
            active={checkRoute('analytics')}
          />

          <SCSidebarItem
            icon={<i className="kt-activities text-grey-darker" />}
            title={
              <NavLink
                title="Activities"
                to="/activities"
                className={sidebarItemClass}
              >
                Activities
              </NavLink>
            }
            active={checkRoute('activities')}
          />
          {liveMonitorModuleEl}
        </div>
        <>
          <div className="menu-item-group-label">Integration</div>
          <SCSidebarItem
            icon={<i className="kt-integration text-grey-darker" />}
            title={
              <NavLink
                title="Integration Status"
                to="/integration-status"
                className={sidebarItemClass}
              >
                Integration Status
              </NavLink>
            }
            active={checkRoute('integration-status')}
          />
          {showPosMapping && (
            <div
              className="cursor-pointer pos-mapping-sidebar-row"
              onClick={() => setPosMappingExpanded(!posMappingExpanded)}
              role="button"
              tabIndex={0}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setPosMappingExpanded(!posMappingExpanded);
                }
              }}
            >
              <SCSidebarItem
                icon={
                  <i
                    className={`kt-arrow-${
                      posMappingExpanded ? 'down' : 'right'
                    } text-grey-darker`}
                    style={{ fontSize: '12px' }}
                  />
                }
                title={
                  <span
                    className={`${sidebarItemClass} text-black font-medium text-sm antialiased`}
                  >
                    POS Mapping
                  </span>
                }
                active={false}
              />
              {posMappingExpanded && (
                <div className="relative ml-6 pl-2 border-l-2 border-gray-200">
                  <SCSidebarItem
                    icon={<i className="kt-tools-kitchen text-grey-darker" />}
                    title={
                      <NavLink
                        title="ESB"
                        to={ESB_MENU_MAPPING_ROUTE}
                        className={sidebarItemClass}
                      >
                        ESB
                      </NavLink>
                    }
                    active={checkRoute('esb-mapping')}
                  />
                  <SCSidebarItem
                    icon={<i className="kt-building-store text-grey-darker" />}
                    title={
                      <div className="flex items-center gap-2 cursor-not-allowed opacity-50">
                        <span className={sidebarItemClass}>Square</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 rounded font-semibold uppercase tracking-wide">
                          Soon
                        </span>
                      </div>
                    }
                    active={false}
                  />
                </div>
              )}
            </div>
          )}
          <SCSidebarItem
            icon={<i className="kt-aggregators text-grey-darker" />}
            title={
              <NavLink
                title="Aggregators"
                to="/provider"
                className={sidebarItemClass}
              >
                Aggregators
              </NavLink>
            }
            active={checkRoute('provider')}
          />
          <SCSidebarItem
            icon={<i className="kt-rider text-grey-darker" />}
            title={
              <NavLink
                title="3PL Integration"
                to={PROVIDER_INTEGRATION_ROUTE}
                className={sidebarItemClass}
              >
                3PL Integration
              </NavLink>
            }
            active={checkRoute('provider-integration')}
          />
          <SCSidebarItem
            icon={<i className="kt-webhooks text-grey-darker" />}
            title={
              <NavLink
                title="Webhooks"
                to="/webhooks"
                className={sidebarItemClass}
              >
                Webhooks
              </NavLink>
            }
            active={checkRoute('webhooks')}
          />
          {webhookReplayModuleEl}
          {paymentIntegrationModuleEl}
          {reviewIntegrationModuleEl}
          {menuPullingModuleEl}
        </>

        <div>
          <div className="menu-item-group-label">Config</div>
          <SCSidebarItem
            icon={<i className="kt-business text-grey-darker" />}
            title={
              <NavLink
                title="Business"
                to="/business"
                className={sidebarItemClass}
              >
                Business
              </NavLink>
            }
            active={checkRoute('business')}
          />
          <SCSidebarItem
            icon={<i className="sc-brand text-grey-darker" />}
            title={
              <NavLink title="Brand" to="/brand" className={sidebarItemClass}>
                Brand
              </NavLink>
            }
            active={checkRoute('brand')}
          />
          {/* <SCSidebarItem
            icon={<i className="kt-location text-grey-darker" />}
            title={
              <NavLink
                title="Location"
                to="/branch"
                className={sidebarItemClass}
              >
                Location
              </NavLink>
            }
            active={checkRoute('branch')}
          /> */}
          <SCSidebarItem
            icon={<i className="kt-location text-grey-darker" />}
            title={
              <NavLink
                title="Location"
                to="/location"
                className={sidebarItemClass}
              >
                Location
              </NavLink>
            }
            active={checkRoute('location')}
          />
          <SCSidebarItem
            icon={<i className="kt-sync text-grey-darker" />}
            title={
              <NavLink
                title="Mirror Order"
                to={MIRROR_ORDER_ROUTE}
                className={sidebarItemClass}
              >
                Mirror Order
              </NavLink>
            }
            active={checkRoute('mirror-order')}
          />
          {addOrderSettingsEl}
          {/* <SCSidebarItem
            icon={<i className="kt-printer text-grey-darker" />}
            title={
              <NavLink
                title="Printer Settings"
                to="/printer-settings"
                className={sidebarItemClass}
              >
                Printer Settings
              </NavLink>
            }
            active={checkRoute('printer-settings')}
          /> */}
          <SCSidebarItem
            icon={<i className="kt-printer text-grey-darker" />}
            title={
              <NavLink
                title="Printer Settings"
                to="/printer-profiles"
                className={sidebarItemClass}
              >
                Printer Settings
              </NavLink>
            }
            active={checkRoute('printer-profiles')}
          />
          {imageManagerModuleEl}
          <SCSidebarItem
            icon={<i className="kt-account-circle text-grey-darker" />}
            title={
              <NavLink title="User" to="/user" className={sidebarItemClass}>
                User
              </NavLink>
            }
            active={checkRoute('user')}
          />
          {invoiceModule}
          {roleSidebarElm}
          {permissionSidebarElm}
          {kaiAdminSidebarElm}
          {featureAccessModuleElm}
        </div>

        {(canManagePartnerAccess || canViewPartnerActivity) && (
          <div>
            <div className="menu-item-group-label">API Integration</div>
            {partnerAccessSidebarElm}
            {apiActivityLogSidebarElm}
          </div>
        )}

        {copyDisclaimer}
      </SCSidebar>
      {children}
      {canViewKai ? <KaiDrawer /> : null}
    </div>
  );
}

Layout.propTypes = {
  appSettings: PropTypes.object,
  businessList: PropTypes.array,
  children: PropTypes.object,
  isKDSViewActive: PropTypes.bool,
  user: PropTypes.object,
};

const mapStateToProps = state => ({
  appSettings: makeSelectGlobalProperty('appSettings')(state),
  businessList: makeSelectGlobalProperty('businessList')(state) || [],
  isKDSViewActive: selectIsKDSViewActive(state),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(Layout);
